import { useCallback } from "react";
import { getCsrfToken } from "@workspace/api-client-react";

const TOKEN_WAIT_MS = 3000;
const TOKEN_POLL_INTERVAL_MS = 50;

/**
 * Resolve the per-session CSRF token, briefly waiting for it to be bootstrapped.
 *
 * The token is fetched once on app startup (see CsrfTokenLoader). The first
 * page_view fires on mount and can race that fetch, so we poll for a short
 * window rather than send the event with an empty token (which the server
 * rejects with 403). Returns null if the token never becomes available.
 */
async function waitForCsrfToken(): Promise<string | null> {
  const existing = getCsrfToken();
  if (existing) return existing;

  const deadline = Date.now() + TOKEN_WAIT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, TOKEN_POLL_INTERVAL_MS));
    const token = getCsrfToken();
    if (token) return token;
  }
  return null;
}

/**
 * Returns a fire-and-forget event tracker that POSTs to /api/analytics/track.
 * Identity is resolved server-side from the session — do NOT send userId here.
 * Silently ignores errors so analytics never breaks the UX.
 *
 * The server enforces CSRF on all mutating requests (global csrfMiddleware), so
 * the per-session x-csrf-token header must be attached or the event is dropped
 * with a 403. We resolve the token via getCsrfToken(), waiting briefly for the
 * startup bootstrap to complete so early events (page_view) are not lost.
 *
 * Use ONLY for: page_view, open_to_work_toggle, profile_cta_click, profile_view.
 * Login events are tracked server-side in auth callbacks.
 */
export function useTrackEvent() {
  const track = useCallback(
    (eventType: string, metadata?: Record<string, unknown>) => {
      void (async () => {
        try {
          const csrfToken = await waitForCsrfToken();
          await fetch("/api/analytics/track", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-csrf-token": csrfToken ?? "",
            },
            body: JSON.stringify({ eventType, ...(metadata ? { metadata } : {}) }),
            credentials: "include",
          });
        } catch {
          // fail-open
        }
      })();
    },
    [],
  );

  return track;
}
